/**
 * EventStreamPanel — live scrolling agent events, color-coded by type.
 */

import { useAgentEvents } from "@/sdk/hooks/use-agents";
import { parsePayload } from "@/sdk/types/agents";
import type { AgentEvent, AgentEventType } from "@/sdk/types/agents";

const EVENT_COLORS: Record<AgentEventType, string> = {
  agent_signal: "text-indigo-400",
  trade_executed: "text-emerald-400",
  trade_closed: "text-cyan-400",
  risk_rejected: "text-red-400",
  risk_approved: "text-emerald-500",
  oracle_consensus: "text-amber-400",
  agent_started: "text-green-400",
  agent_stopped: "text-zinc-400",
  agent_error: "text-red-500",
  position_update: "text-blue-400",
};

const EVENT_ICONS: Record<AgentEventType, string> = {
  agent_signal: ">>",
  trade_executed: "$$",
  trade_closed: "xx",
  risk_rejected: "!!",
  risk_approved: "ok",
  oracle_consensus: "**",
  agent_started: "++",
  agent_stopped: "--",
  agent_error: "EE",
  position_update: "~~",
};

function EventRow({ event }: { event: AgentEvent }) {
  const color = EVENT_COLORS[event.event_type] ?? "text-zinc-400";
  const icon = EVENT_ICONS[event.event_type] ?? "..";
  const time = new Date(event.created_at).toLocaleTimeString();
  // payload arrives as a JSON string (asyncpg JSONB-as-text) or null.
  const payload = parsePayload(event.payload);
  const summary =
    (typeof payload.question === "string" ? payload.question : undefined) ??
    (typeof payload.reason === "string" ? payload.reason : undefined) ??
    event.event_type.replace(/_/g, " ");

  return (
    <div className="flex items-start gap-2 px-2 py-0.5 text-xs font-mono hover:bg-zinc-900/50">
      <span className="w-16 shrink-0 text-zinc-600">{time}</span>
      <span className={`w-5 shrink-0 font-bold ${color}`}>{icon}</span>
      <span className="w-16 shrink-0 text-zinc-500 truncate">{event.agent_id}</span>
      <span className="text-zinc-300 truncate">{summary}</span>
    </div>
  );
}

export function EventStreamPanel() {
  const { data, isLoading, isError } = useAgentEvents(100);
  const events = data?.events ?? [];

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-zinc-500">
        Connecting to event stream...
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col bg-zinc-950">
      <div className="flex items-center justify-between border-b border-zinc-800 px-2 py-1">
        <span className="text-[10px] uppercase tracking-wider text-zinc-600">
          Live Events ({events.length})
        </span>
      </div>
      <div className="flex-1 overflow-auto">
        {events.length === 0 ? (
          // An errored read is not an empty feed — say so.
          isError ? (
            <div className="mt-8 text-center text-xs text-amber-400">
              Event feed unavailable — backend unreachable
            </div>
          ) : (
            <div className="mt-8 text-center text-xs text-zinc-600">
              No events yet
            </div>
          )
        ) : (
          events.map((e) => <EventRow key={e.id} event={e} />)
        )}
      </div>
    </div>
  );
}
